import {
  ContentLayout,
  Alert,
  Box,
  FormField,
  SpaceBetween,
  Header,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useState } from 'react';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import PermissionList from 'src/components/permission/PermissionList';
import RoleListDropdown from 'src/components/roles/RoleListDropdown';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { Persona } from 'src/contstant/Persona';
import useUserProfile from 'src/hooks/useUserProfile';
import { usePermission } from 'src/store/permission/usePermission';
import { useRoles } from 'src/store/role/useRoles';
import { useUsers } from 'src/store/users/useUsers';
import { Role, UpdateRoleRequest } from 'src/types/orbit/Roles';
import { checkFeaturePermission } from 'src/utils/generalUtils';

const PermissionsPage = () => {
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const {
    loadRoles,
    fetchRolesApiStatus,
    roles,
    updateRole,
    updateRolesApiStatus,
    clearUpdateStatusState,
  } = useRoles();
  const {
    userPermission,
    checkUserPermissionApiStatus,
    checkUserPermissions,
    currentUser,
  } = useUsers();
  const { getPersona, getUserId, getUserBusinessUnitId, getUserProgramId, getDivisions } =
    useUserProfile();
  const { loadPermission, fetchPermissionsApiStatus, permissionsList } = usePermission();

  const isEditRoles = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.PERMISSIONS.EDIT_PERMISSIONS,
      userPermission?.permissions,
    );
  }, [userPermission?.permissions, getPersona]);

  const getFinalRoleList = useCallback(() => {
    return roles.filter((item: Role) => item.roleId !== Persona.PLATFORM_ADMIN);
  }, [roles]);

  useEffect(() => {
    if (getPersona() !== Persona.PLATFORM_ADMIN) {
      checkUserPermissions({
        userId: getUserId(),
        programId: getUserProgramId(),
        businessUnit: getUserBusinessUnitId(),
        permissions: [permissionAccessConfig.PERMISSIONS.EDIT_PERMISSIONS],
        divisions: getDivisions(),
      });
    }

    loadPermission({
      programId: getUserProgramId(),
      isActive: true,
      canAddToRole: true,
    });
    loadRoles({
      businessUnitId: getUserBusinessUnitId()!,
      programId: getUserProgramId(),
      isRoleActive: true,
      persona: getPersona(),
    });
  }, [currentUser]);

  useEffect(() => {
    if (getFinalRoleList() && getFinalRoleList().length > 0 && !selectedRole) {
      if (getPersona() !== Persona.PLATFORM_ADMIN) {
        setSelectedRole(
          getFinalRoleList().find((item: Role) => item.roleId === getPersona())!,
        );
      } else {
        setSelectedRole(
          getFinalRoleList().find(
            (item: Role) => item.roleId === Persona.BUSINESS_UNIT_ADMIN,
          )!,
        );
      }
    } else {
      setSelectedRole(
        getFinalRoleList().find((item: Role) => item.roleId === selectedRole?.roleId)!,
      );
    }
  }, [getFinalRoleList()]);

  const onRoleChange = (role: Role) => {
    setSelectedRole(role);
  };

  const saveRole = async (
    role: Role,
    defaultPermissions?: string[] | null,
    overriddenPermission?: string[] | null,
  ) => {
    const data: UpdateRoleRequest = {
      programId: getUserProgramId(),
      businessUnitId: getUserBusinessUnitId()!,
      roleId: role.roleId,
      defaultPermissions: defaultPermissions,
      overriddenPermission: overriddenPermission,
      updatedBy: getUserId(),
      isRoleActive: true,
      persona: getPersona(),
    };
    updateRole(data);
  };

  return (
    <ContentLayout>
      <Box margin={{ top: 's' }}>
        <SpaceBetween size="l">
          <ApiStatusFlashBar
            apiStatus={[updateRolesApiStatus]}
            clearMessages={clearUpdateStatusState}
          />

          <div style={{ maxWidth: '400px' }}>
            <Box margin={{ bottom: 'xs' }}>
              <Header variant="h3">Role</Header>
            </Box>
            <RoleListDropdown
              isLoading={fetchRolesApiStatus.isLoading}
              roleList={getFinalRoleList()}
              error={fetchPermissionsApiStatus.errorMessage}
              selectedRole={selectedRole}
              onRoleChange={onRoleChange}
            />
          </div>

          {!isEditRoles() && !checkUserPermissionApiStatus.isLoading && (
            <Alert type="error" header="You dont have permssion to manage roles."></Alert>
          )}

          <PermissionList
            title="Default Permissions"
            isLoading={fetchPermissionsApiStatus.isLoading}
            footerEnabled={getPersona() === Persona.PLATFORM_ADMIN ? true : false}
            selectedOldPermissions={selectedRole?.defaultPermissions}
            permissions={permissionsList || []}
            footerButtonLoader={updateRolesApiStatus.isLoading}
            showSelectedPermissionsOnly={
              getPersona() === Persona.PLATFORM_ADMIN ? false : true
            }
            readOnly={
              getPersona() === Persona.PLATFORM_ADMIN
                ? selectedRole?.roleId === Persona.PLATFORM_ADMIN
                : true
            }
            error={fetchPermissionsApiStatus.errorMessage}
            onChangePermission={(data) => {}}
            onSaveClick={(selectedPermissions: any) => {
              saveRole(selectedRole!, selectedPermissions, null);
            }}
          />

          {getPersona() !== Persona.PLATFORM_ADMIN && (
            <PermissionList
              footerButtonLoader={updateRolesApiStatus.isLoading}
              isLoading={fetchPermissionsApiStatus.isLoading}
              title="Additional Permissions"
              excludePermissions={selectedRole?.defaultPermissions}
              selectedOldPermissions={selectedRole?.overriddenPermission || []}
              permissions={permissionsList || []}
              readOnly={!(isEditRoles() && selectedRole?.roleId !== getPersona())}
              footerEnabled={isEditRoles() && selectedRole?.roleId !== getPersona()}
              error={fetchPermissionsApiStatus.errorMessage}
              onChangePermission={() => {}}
              onSaveClick={(selectedPermissions: any) => {
                if (selectedRole) {
                  saveRole(selectedRole!, null, selectedPermissions);
                } else {
                  alert('Please select any role');
                }
              }}
            />
          )}
        </SpaceBetween>
      </Box>
    </ContentLayout>
  );
};

export default PermissionsPage;
